import { useColorMode } from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BellFill,
  BellLine,
  Chat,
  ChatFill,
  Feed,
  FeedFill,
  ProfileFill,
  ProfileLine
} from '@peacecover/icon';

import { useFeatureEnabled } from '@pocapan/shared/domains/features/ui/hooks/useFeatureEnabled';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';

const ENABLED_COLOR = 'text-primary-light dark:text-primary-dark';
const DISABLED_COLOR = 'text-text-main-lv4';

export default function HomeBottomNav({ tabIndex }: { tabIndex: number }) {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const [selectedTabIndex, setSelectedTabIndex] = useState(tabIndex ?? 0);

  const { data: cardReviewFeature } = useFeatureEnabled(FEATURE_KEYS.CARD_REVIEW);
  const isCardReviewEnabled = cardReviewFeature?.featureFlag?.enabled ?? false;

  useEffect(() => {
    setSelectedTabIndex(tabIndex);
  }, [tabIndex]);

  const setEnabledColor = (isEnabled: boolean) => {
    return isEnabled
      ? 'text-primary-light dark:text-primary-dark'
      : 'text-main-light-4 dark:text-main-dark-4';
  };

  const TABS = useMemo(() => {
    const ALL_TABS = [
      {
        index: 0,
        inActiveIcon: <Feed className={DISABLED_COLOR} width={26} height={26} />,
        activeIcon: <FeedFill className={ENABLED_COLOR} width={26} height={26} />,
        name: t('tabs.home')
      },
      {
        index: 1,
        inActiveIcon: <Chat className={DISABLED_COLOR} width={26} height={26} />,
        activeIcon: <ChatFill className={ENABLED_COLOR} width={26} height={26} />,
        name: isCardReviewEnabled ? '베티' : t('tabs.chat')
      },
      {
        index: 2,
        inActiveIcon: <BellLine className={DISABLED_COLOR} width={26} height={26} />,
        activeIcon: <BellFill className={ENABLED_COLOR} width={26} height={26} />,
        name: t('tabs.notifications')
      },
      {
        index: 3,
        inActiveIcon: <ProfileLine className={DISABLED_COLOR} width={26} height={26} />,
        activeIcon: <ProfileFill className={ENABLED_COLOR} width={26} height={26} />,
        name: t('tabs.profile')
      }
    ];

    return ALL_TABS;
  }, [isCardReviewEnabled, t]);

  const onClickTabButton = (index: number) => {
    setSelectedTabIndex(index);
    if (selectedTabIndex === index) {
      return window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    switch (index) {
      case 0:
        return navigate('/home');
      case 1:
        return navigate('/feed');
      case 2:
        return navigate('/notifications');
      case 3:
        return navigate('/my');
      default:
        return;
    }
  };

  return (
    <div
      className={`${
        colorMode === 'dark' ? 'darkBottomNavbar' : 'bottomNavbar'
      } dark:!bg-background-elevated safeAreaPaddingBottom z-[999] max-w-[var(--chakra-sizes-xl)]`}
      style={
        colorMode === 'dark'
          ? { filter: 'drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.3))' }
          : { filter: 'drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.1))' }
      }
    >
      <div
        className="bg-background-light dark:bg-[#151619] px-[10px] grid"
        style={{ gridTemplateColumns: `repeat(${TABS.length}, 1fr)` }}
      >
        {TABS.map((tab) => {
          return (
            <button
              key={tab.index}
              type="button"
              className="pt-[13px] pb-[10px] w-full h-full flex-1"
              onClick={() => onClickTabButton(tab.index)}
              style={{
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                outline: 'none'
              }}
            >
              <div className="flex flex-col items-center gap-[2px]">
                {selectedTabIndex === tab.index ? tab.activeIcon : tab.inActiveIcon}
                <p
                  className={`${setEnabledColor(selectedTabIndex === tab.index)} text-[11px] font-medium`}
                >
                  {tab.name}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
